import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ==================== Import Course ====================

/**
 * Upload a course JSON or CDM file
 * @param {File} file - The JSON or CDM file to upload (.cdm files are ZIP archives containing course.json)
 * @param {string|null} userId - Optional user ID
 * @returns {Promise<Object>} - Upload response with session ID and counts
 */
export const uploadCourse = async (file, userId = null) => {
  console.log('importApi: uploadCourse called with file:', file, 'userId:', userId);
  console.log('importApi: API_URL:', API_URL);
  
  const formData = new FormData();
  formData.append('file', file);
  if (userId) {
    formData.append('userId', userId);
  }

  console.log('importApi: FormData created, posting to:', `${API_URL}/import/upload`);
  
  try {
    const response = await axios.post(`${API_URL}/import/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('importApi: response received:', response);
    return response.data;
  } catch (error) {
    console.error('importApi: error during upload:', error);
    throw error;
  }
};

/**
 * Send message during import conversation phase
 * @param {string} sessionId - Import session ID
 * @param {string} message - User's message
 * @returns {Promise<Object>} - Response with optional plan_generated flag
 */
export const sendConversationMessage = async (sessionId, message) => {
  const response = await axios.post(`${API_URL}/import/conversation`, {
    sessionId,
    message,
  });

  return response.data;
};

/**
 * Approve the generated execution plan and start execution mode
 * @param {string} sessionId - Import session ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Course ID and execution plan details
 */
export const approvePlan = async (sessionId, userId = null) => {
  const response = await axios.post(`${API_URL}/import/approve-plan`, {
    sessionId,
    userId,
  });

  return response.data;
};

/**
 * Send message during plan execution (step-by-step enhancement)
 * @param {string} sessionId - Import session ID
 * @param {string} message - User's message
 * @returns {Promise<Object>} - Agent response
 */
export const sendExecutionMessage = async (sessionId, message) => {
  const response = await axios.post(`${API_URL}/import/execute`, {
    sessionId,
    message,
  });

  return response.data;
};

/**
 * Move to the next step in execution plan
 * @param {string} sessionId - Import session ID
 * @returns {Promise<Object>} - Next step details
 */
export const moveToNextStep = async (sessionId) => {
  const response = await axios.post(`${API_URL}/import/next-step`, {
    sessionId,
  });

  return response.data;
};

/**
 * Confirm goals before starting the import conversation phase.
 * @param {string} sessionId - Import session ID
 * @param {Array} confirmedGoals - Finalized goals list
 * @returns {Promise<Object>} - Confirmation response
 */
export const confirmGoals = async (sessionId, confirmedGoals = []) => {
  const response = await axios.post(`${API_URL}/import/confirm-goals`, {
    sessionId,
    confirmedGoals,
  });

  return response.data;
};

/**
 * Get chunked course data (for large courses)
 * @param {string} sessionId - Import session ID
 * @returns {Promise<Object>} - Chunked data or full data
 */
export const getChunkedCourseData = async (sessionId) => {
  const response = await axios.get(`${API_URL}/import/session/${sessionId}/chunked-data`);

  return response.data;
};

/**
 * Get complete session summary
 * @param {string} sessionId - Import session ID
 * @returns {Promise<Object>} - Session, analysis, and plan data
 */
export const getSessionSummary = async (sessionId) => {
  const response = await axios.get(`${API_URL}/import/session/${sessionId}`);

  return response.data;
};


